import type {
  WriterCreative,
  WriterCreativeChannel,
  WriterCreativeClass,
  WriterCreativePopulationType,
} from "components/modals/add-creatives-with-writer-modal/add-creatives-with-writer-modal.types";

// Mirrors CampaignStrategySchemaConstants.MIN_SCHEMA_VERSION_WITH_CREATIVE_ID on the backend:
// creative items only get a stable, system-assigned `id` from this schema version onward.
const MIN_SCHEMA_VERSION_WITH_CREATIVE_ID = 3;

const WRITER_CREATIVE_CHANNELS: WriterCreativeChannel[] = ["email", "sms", "web-page", "form"];

const isWriterCreativeChannel = (value: unknown): value is WriterCreativeChannel =>
  typeof value === "string" && WRITER_CREATIVE_CHANNELS.includes(value as WriterCreativeChannel);

const parseClasses = (value: unknown): WriterCreativeClass[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .filter((item): item is string => typeof item === "string")
    .map((item) => item.toUpperCase() as WriterCreativeClass);
};

const parseCreativeItem = (
  item: unknown,
  populationType: WriterCreativePopulationType,
): WriterCreative | null => {
  if (!item || typeof item !== "object") {
    return null;
  }

  const record = item as Record<string, unknown>;

  if (typeof record.id !== "string" || !record.id || !isWriterCreativeChannel(record.channel)) {
    return null;
  }

  return {
    id: record.id,
    name: typeof record.name === "string" && record.name ? record.name : "Creative",
    touchpointChannel: record.channel,
    populationType,
    classes: parseClasses(record.classes),
  };
};

const getPopulationCreativeItems = (
  strategyData: Record<string, unknown>,
  audienceKey: string,
  populationType: WriterCreativePopulationType,
): WriterCreative[] => {
  const creatives = strategyData.creatives;
  if (!creatives || typeof creatives !== "object") {
    return [];
  }

  const audience = (creatives as Record<string, unknown>)[audienceKey];
  if (!audience || typeof audience !== "object") {
    return [];
  }

  const items = (audience as Record<string, unknown>).items;
  if (!Array.isArray(items)) {
    return [];
  }

  return items
    .map((item) => parseCreativeItem(item, populationType))
    .filter((item): item is WriterCreative => item !== null);
};

export const filterWriterCreativesByAllowedChannels = (
  creatives: WriterCreative[],
  allowedChannels: string[] | undefined,
): WriterCreative[] => {
  if (!allowedChannels) {
    return creatives;
  }

  const allowedChannelsSet = new Set(allowedChannels);

  return creatives.filter((creative) => allowedChannelsSet.has(creative.touchpointChannel));
};

/**
 * Parses the writer-selectable creative items out of a templated campaign's `strategyData`.
 * Mirrors `CampaignTemplateCreativeItemConverter.getCreatives` on the backend.
 */
export const getWriterCreativesFromStrategyData = (
  strategyData: unknown,
  schemaVersion: number | undefined,
): WriterCreative[] => {
  if (
    !strategyData ||
    typeof strategyData !== "object" ||
    schemaVersion == null ||
    schemaVersion < MIN_SCHEMA_VERSION_WITH_CREATIVE_ID
  ) {
    return [];
  }

  const record = strategyData as Record<string, unknown>;

  return [
    ...getPopulationCreativeItems(record, "student", "STUDENT"),
    ...getPopulationCreativeItems(record, "parent", "PARENT"),
  ];
};
